import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ErrorParser } from '@app/classes';
import { AuthService, NotifyService } from '@app/services';
import { ShowView } from '@common/views';
import { Office } from '@models/company/office.model';
import { Employee } from '@models/employee/employee.model';
import { Tenant } from '@models/payroll/tenant.model';
import { TenantCreationStatus } from '@payroll/enums/tenant-creation-status.enum';
import { Schedule } from '@payroll/interfaces/schedule.interface';
import { PayrollResources } from '@payroll/payroll.resources';

@Component({
    templateUrl: './debug-show.template.html',
})
export class DebugShowView extends ShowView implements OnInit {
    isLoading = false;
    employees: Employee[] = [];
    offices: Office[] = [];
    syncErrors: any = false;
    tenantCreationStatus: 'in_progress' | 'success' | 'error';
    openTenantDialog: boolean = false;

    constructor(
        private notify: NotifyService,
        public auth: AuthService,
        private http: HttpClient
    ) {
        super();
    }

    onCancel(): void {}

    onDelete(): void {}

    ngOnInit(): void {
        this.isLoading = true;
        this.auth
            .hydrate()
            .then(() =>
                Promise.all([
                    Employee.param('company', this.auth.company.id)
                        .whereIn('status', ['active', 'terminated', 'on leave'])
                        .all()
                        .then(([employees]) => {
                            this.employees = employees;
                        }),
                    this.setTenantCreationStatus(),
                ])
            )
            .then(() => (this.isLoading = false));
    }

    onSave(): void {}

    toggle(toggled: boolean, employee: Employee): void {
        employee.isPayrollSyncEnabled = toggled;
        employee
            .param('company', this.auth.company.id)
            .save()
            .then(() => {
                this.isLoading = true;
                this.notify.success('Updated');
                this.ngOnInit();
            })
            .catch((err) => {
                this.isLoading = false;
                this.notify.error(err.message);
            });
    }

    addEmployee(employee: Employee): void {
        this.isLoading = true;
        employee.isPayrollSyncEnabled = true;
        employee
            .param('company', this.auth.company.id)
            .save()
            .then(() => {
                this.isLoading = false;
                this.notify.success('Updated');
                this.ngOnInit();
            })
            .catch((err) => {
                this.isLoading = false;
                this.notify.error(err.message);
            });
    }

    updateEmployee(employee: Employee): void {
        this.isLoading = true;
        employee.isPayrollSyncEnabled = true;
        employee
            .param('company', this.auth.company.id)
            .save()
            .then(() => {
                this.isLoading = false;
                this.notify.success('Updated');
                this.ngOnInit();
            })
            .catch((err) => {
                this.isLoading = false;
                this.notify.error(err.message);
            });
    }

    syncAll(): void {
        this.isLoading = true;
        this.http
            .put(PayrollResources.Sync, {})
            .toPromise()
            .then(() => {
                this.notify.success('Updated');
                this.syncErrors = false;
                this.ngOnInit();
            })
            .catch((err) => {
                this.notify.error('Some entities could not be synced');
                this.syncErrors = err.error;
                this.isLoading = false;
            });
    }

    syncAllCompensations(): void {
        this.isLoading = true;
        this.http
            .put(PayrollResources.SyncCompensation, {})
            .toPromise()
            .then(() => {
                this.notify.success('Updated');
                this.syncErrors = false;
                this.ngOnInit();
            })
            .catch((err) => {
                this.notify.error('Some entities could not be synced');
                this.syncErrors = err.error;
                this.isLoading = false;
            });
    }

    syncAllEmployees(): void {
        this.isLoading = true;
        this.http
            .put(PayrollResources.SyncEmployee, {})
            .toPromise()
            .then(() => {
                this.notify.success('Updated');
                this.syncErrors = false;
                this.ngOnInit();
            })
            .catch((err) => {
                this.notify.error('Some entities could not be synced');
                this.syncErrors = err.error;
                this.isLoading = false;
            });
    }

    createTenant(tenantForm: NgForm, schedule: Schedule): void {
        // Do not submit form if not valid
        if (!tenantForm.valid) {
            return;
        }
        this.isLoading = true;
        const payload = {
            data: {
                type: 'createTenant',
                attributes: schedule,
            },
        };
        this.http
            .post(PayrollResources.Tenant, payload)
            .toPromise()
            .then(() => {
                this.notify.success('Created');
                this.ngOnInit();
                window.location.reload();
            })
            .catch((err) => {
                this.notify.error(ErrorParser.parse(err.error) || 'Error creating tenant');
                this.isLoading = false;
            });
    }

    private async setTenantCreationStatus(): Promise<void> {
        if (!this.auth.company.prTenantId) {
            this.tenantCreationStatus = undefined;
            return;
        }

        Tenant.find(this.auth.company.prTenantId)
            .then((tenant: Tenant) => {
                switch (tenant.creationStatus) {
                    case TenantCreationStatus.INITIALIZED:
                    case TenantCreationStatus.SCHEMA_CREATION:
                    case TenantCreationStatus.SYNCING:
                        return 'in_progress';
                    case TenantCreationStatus.SCHEMA_CREATED:
                    case TenantCreationStatus.SYNCED:
                        return 'success';
                    case TenantCreationStatus.ERROR:
                        return 'error';
                }
            })
            .then((status) => (this.tenantCreationStatus = status));
    }
}
